import { useState, useEffect, useContext, useRef } from "react";

// react-router components
import { Link } from "react-router-dom";
import { useLocation, NavLink, useNavigate } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui material components
import Container from "@mui/material/Container";
import Icon from "@mui/material/Icon";
import Badge from "@mui/material/Badge";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import Divider from "@mui/material/Divider";
import Tooltip from "@mui/material/Tooltip";
import SearchIcon from "@mui/icons-material/Search";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import ChatOutlinedIcon from "@mui/icons-material/ChatOutlined";
import PermMediaIcon from "@mui/icons-material/PermMedia";
import InsertInvitationOutlinedIcon from "@mui/icons-material/InsertInvitationOutlined";

import Settings from "@mui/icons-material/Settings";
import Logout from "@mui/icons-material/Logout";
import MailIcon from "@mui/icons-material/Mail";
import NotificationsIcon from "@mui/icons-material/Notifications";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import MessageIcon from "@mui/icons-material/Message";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import ContactMailIcon from "@mui/icons-material/ContactMail";

// Soft UI Dashboard PRO React components
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiSelect from "components/SuiSelect";
import { Input, Select } from "antd";

import SuiButton from "components/SuiButton";

// Soft UI Dashboard PRO React example components
import DefaultNavbarLink from "examples/Navbars/DefaultNavbar/DefaultNavbarLink";
import DefaultNavbarMobile from "examples/Navbars/DefaultNavbar/DefaultNavbarMobile";

// Soft UI Dashboard PRO React base styles
import breakpoints from "assets/theme/base/breakpoints";

// DefaultNavbar dropdown menus
import MenuPopper1 from "examples/Navbars/DefaultNavbar/Menus/MenuPopper1";

import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarIconButton,
  navbarDesktopMenu,
  navbarMobileMenu,
} from "examples/Navbars/DashboardNavbar/styles";

import { StoreContext } from "context/store";
import CounterContext from "context/counter";
import { AccountContext } from "context/account";
import MyProjectsContext from "context/my-projects";

import Avatar from "@mui/material/Avatar";
import SuiAvatar from "components/SuiAvatar";
import { stringToColor } from "utils/color";
import UpStyledLogo from "components/UpStyled/UpStyledLogo";
import { MAX_SCREEN_WIDTH } from "utils/data";
import Prompt from "components/UpStyled/Prompt";
import { externalLinks } from "utils/data";
import RssFeedIcon from "@mui/icons-material/RssFeed";
import ContactSupportIcon from "@mui/icons-material/ContactSupport";
import PostAddIcon from "@mui/icons-material/PostAdd";
import SmartDisplayIcon from "@mui/icons-material/SmartDisplay";
import TutorialModal from "pages/tutorial/TutorialModal";

const REACT_APP_DEV_MENU = process.env.REACT_APP_DEV_MENU;

function DefaultNavbar({ routes, transparent, light, rightDrawer, setRightDrawer }) {
  const [pagesMenu, setPagesMenu] = useState(false);

  const [mobileNavbar, setMobileNavbar] = useState(false);
  const [mobileView, setMobileView] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [openAccountMenu, setOpenAccountMenu] = useState(false);
  const [searchAnchor, setSearchAnchor] = useState(null);
  const [openSearchMenu, setOpenSearchMenu] = useState(false);
  const [searchType, setSearchType] = useState("style");
  const [searchModalVisible, setSearchModalVisible] = useState(false);
  const [tutorialVisible, setTutorialVisible] = useState(false);

  const { stagesProjects, prospects } = useContext(MyProjectsContext);
  const account = useContext(AccountContext);
  const { store } = useContext(StoreContext);
  const counter = useContext(CounterContext);
  const { getNewMessageCount, count, ncount } = counter;

  const location = useLocation();
  const { pathname } = location;
  const collapseName = pathname.split("/").slice(1)[0];
  const navigate = useNavigate();

  const searchInputRef = useRef(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    setOpenAccountMenu(true);
  };
  const handleClose = () => {
    setAnchorEl(null);
    setOpenAccountMenu(false);
  };

  const handleOpenMenu = (e, name) => {
    setPagesMenu(name);
    setAnchorEl(e.currentTarget);
  };
  const handleCloseMenu = (e, route) => {
    setPagesMenu(false);
    setAnchorEl(null);
  };

  //==============================================================
  //handle search menu
  const handleSearchMenuClick = (event) => {
    setSearchAnchor(event.currentTarget);
    setOpenSearchMenu(true);
  };
  const handleCloseSearchMenu = () => {
    setSearchAnchor(null);
    setOpenSearchMenu(false);
  };

  //----------------------------------------------------------------

  const openMobileNavbar = ({ currentTarget }) => setMobileNavbar(currentTarget.parentNode);
  const closeMobileNavbar = () => setMobileNavbar(false);

  useEffect(() => {
    // A function that sets the display state for the DefaultNavbarMobile.
    function displayMobileNavbar() {
      if (window.innerWidth < breakpoints.values.lg) {
        setMobileView(true);
        setMobileNavbar(false);
      } else {
        setMobileView(false);
        setMobileNavbar(false);
      }
    }

    /** 
     The event listener that's calling the displayMobileNavbar function when 
     resizing the window.
    */
    window.addEventListener("resize", displayMobileNavbar);

    // Call the displayMobileNavbar function to set the state with the initial value.
    displayMobileNavbar();

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", displayMobileNavbar);
  }, []);

  useEffect(() => {
    if (account.id) {
      getNewMessageCount();
      if (account.account_type == "de") {
        setSearchType("style");
      } else {
        setSearchType("project");
      }
    }
  }, [account.id]);

  function stringAvatar(name) {
    return {
      sx: {
        bgcolor: stringToColor(name),
        width: 24,
        height: 24,
      },
      children: `${name && name.length > 0 ? name[0].toUpperCase() : "U"}`,
    };
  }

  const renderSearchMenu = () => (
    <Menu
      anchorEl={searchAnchor}
      id="search-menu"
      open={openSearchMenu}
      onClose={handleCloseSearchMenu}
      onClick={handleCloseSearchMenu}
      PaperProps={{
        elevation: 0,
        sx: {
          overflow: "visible",
          filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
          mt: 1.5,
          "&:before": {
            content: '""',
            display: "block",
            position: "absolute",
            top: 0,
            right: 14,
            width: 10,
            height: 10,
            bgcolor: "background.paper",
            transform: "translateY(-50%) rotate(45deg)",
            zIndex: 0,
          },
        },
      }}
      sx={{ zIndex: 12000 }}
      transformOrigin={{ horizontal: "right", vertical: "top" }}
      anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
    >
      <MenuItem onClick={() => setSearchType("style")}>Style</MenuItem>
      {/* <MenuItem onClick={() => setSearchType("collection")}>Collection</MenuItem> */}
      <MenuItem onClick={() => setSearchType("project")}>Project</MenuItem>
      <MenuItem onClick={() => setSearchType("manufacturer")}>Manufacturer</MenuItem>
      <MenuItem onClick={() => setSearchType("post")}>Post</MenuItem>
    </Menu>
  );

  const renderAccountMenu = () => (
    <Menu
      anchorEl={anchorEl}
      id="account-menu"
      open={openAccountMenu}
      onClose={handleClose}
      onClick={handleClose}
      PaperProps={{
        elevation: 0,
        sx: {
          overflow: "visible",
          filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
          mt: 1,
          "& .MuiAvatar-root": {
            width: 32,
            height: 32,
            ml: -0.5,
            mr: 1,
          },
          "&:before": {
            content: '""',
            display: "block",
            position: "absolute",
            top: 0,
            right: 14,
            width: 10,
            height: 10,
            bgcolor: "background.paper",
            transform: "translateY(-50%) rotate(45deg)",
            zIndex: 0,
          },
        },
      }}
      sx={{ zIndex: 12000 }}
      transformOrigin={{ horizontal: "right", vertical: "top" }}
      anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
    >
      <MenuItem component={Link} to="/wall/portfolio">
        <ListItemIcon>
          <PermMediaIcon fontSize="small" />
        </ListItemIcon>
        My Portfolio
      </MenuItem>
      <MenuItem component={Link} to="/my-posts">
        <ListItemIcon>
          <PostAddIcon fontSize="small" />
        </ListItemIcon>
        My Posts
      </MenuItem>

      <MenuItem component={Link} to="/edit-profile">
        <SuiAvatar
          src={store?.metadata?.avatar}
          variant="circle"
          style={{ width: 24, height: 24, marginRight: 16 }}
          shadow="sm"
        />{" "}
        Edit Profile
      </MenuItem>

      {account.account_type === "de" && (
        <MenuItem component={Link} to="/setting#invite-designer">
          <ListItemIcon>
            <ContactMailIcon fontSize="small" />
          </ListItemIcon>
          Invite Friends
        </MenuItem>
      )}

      <Divider />
      <MenuItem component={Link} to="/messages">
        <ListItemIcon>
          <EmailOutlinedIcon fontSize="small" />
        </ListItemIcon>
        Messages
      </MenuItem>
      <MenuItem component={Link} to="/calendar">
        <ListItemIcon>
          <InsertInvitationOutlinedIcon fontSize="small" />
        </ListItemIcon>
        My Calendar
      </MenuItem>
      <MenuItem component={Link} to="/contactus">
        <ListItemIcon>
          <ChatOutlinedIcon fontSize="small" />
        </ListItemIcon>
        Contact Us
      </MenuItem>
      <MenuItem onClick={() => setTutorialVisible(true)}>
        <ListItemIcon>
          <SmartDisplayIcon fontSize="small" />
        </ListItemIcon>
        Tutorial videos
      </MenuItem>
      <Divider />

      <MenuItem component={Link} to="/setting">
        <ListItemIcon>
          <SettingsOutlinedIcon fontSize="small" />
        </ListItemIcon>
        Settings
      </MenuItem>

      <MenuItem component={Link} to="/social/blog">
        <ListItemIcon>
          <RssFeedIcon fontSize="small" />
        </ListItemIcon>
        Blog
      </MenuItem>
      <MenuItem component={"a"} href={externalLinks["help"]} target="_blank">
        <ListItemIcon>
          <HelpOutlineIcon fontSize="small" />
        </ListItemIcon>
        Help Center
      </MenuItem>

      <Divider />
      <MenuItem
        onClick={() => {
          account.handleLogout();
        }}
      >
        <ListItemIcon>
          <Logout fontSize="small" />
        </ListItemIcon>
        Logout
      </MenuItem>
    </Menu>
  );

  // Render all the routes from the routes.js (All the visible items on the Sidenav)
  const renderRoutes = routes.map((item, index) => {
    let name = item.name;
    if (item.countKey && counter[item.countKey]) {
      item["icon"] = (
        <Badge color="error" variant="dot" invisible={counter[item.countKey] <= 0}>
          {item.icon}
        </Badge>
      );
    }
    if (item.projectCount) {
      if (item.projectCount == "prospects") {
        name = item["name"] + `(${prospects.length})`;
      } else {
        if (stagesProjects[item.projectCount]) {
          name = item["name"] + `(${stagesProjects[item.projectCount].length})`;
        }
      }
    }

    if (item.hint) {
      name = (
        <Tooltip content={item.hint}>
          <SuiTypography>{name}</SuiTypography>
        </Tooltip>
      );
    }

    let returnValue;
    if (REACT_APP_DEV_MENU != "true") {
      //production env
      if (item.dev_menu) {
        //hide development menu
        return;
      }
    }

    let is_hidden = item.hidden || false;
    if (item?.visible_users && item?.visible_users.includes(account.id)) {
      is_hidden = false;
    }

    if (item.account_type && item.account_type != account.account_type) {
      return;
    }

    if (item.type === "collapse" && !is_hidden && item.name) {
      if (item.noCollapse && item.route) {
        returnValue = (
          <Link to={item.route} key={item.key}>
            <DefaultNavbarLink
              name={name}
              light={light}
              collapse={item.collapse}
              active={
                item.key === collapseName ||
                item.key2 == collapseName ||
                (item.keys && item.keys.includes(collapseName))
              }
            />
          </Link>
        );
      } else {
        returnValue = (
          <DefaultNavbarLink
            name={name}
            light={light}
            collapse={item.collapse}
            openHandler={(e) => {
              handleOpenMenu(e, item.name);
            }}
            closeHandler={handleCloseMenu}
            active={
              item.key === collapseName ||
              item.key2 == collapseName ||
              (item.keys && item.keys.includes(collapseName))
            }
          >
            {anchorEl && pagesMenu == item.name ? (
              <MenuPopper1
                routes={item.collapse}
                open={anchorEl}
                close={handleCloseMenu}
                key={item.name}
                mobileMenu={false}
              />
            ) : null}
          </DefaultNavbarLink>
        );
      }
    }

    return returnValue;
  });

  const onSearch = (v) => {
    let searchValue = searchInputRef?.current?.input?.value || "";
    searchValue = searchValue.trim();
    if (searchType) {
      setSearchModalVisible(false);
      navigate(`/search?type=${searchType}&keyword=${searchValue}`);
    }
  };

  return (
    <SuiBox
      width="100%"
      px={4}
      py={1}
      mb={2}
      shadow={transparent ? "none" : "md"}
      color={light ? "white" : "dark"}
      display="flex"
      justifyContent="center"
      alignItems="center"
      // position="absolute"
      left={0}
      zIndex={3}
      sx={({ palette: { transparent: transparentColor, white }, functions: { rgba } }) => ({
        backgroundColor: transparent ? transparentColor.main : rgba(white.main, 0.8),
        backdropFilter: transparent ? "none" : `saturate(200%) blur(30px)`,
        position: "sticky",
        top: 0,
      })}
    >
      {" "}
      <SuiBox
        color="inherit"
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        width="100%"
        sx={{ maxWidth: { lg: MAX_SCREEN_WIDTH + 100, md: MAX_SCREEN_WIDTH - 20 } }}
        my={0}
        mx={2}
        gap={1}
        p={0}
      >
        <SuiBox
          component={Link}
          to="/"
          py={transparent ? 1.5 : 0.75}
          lineHeight={1}
          sx={{ width: 200 }}
        >
          <UpStyledLogo sx={{ height: 35 }} />
        </SuiBox>
        <SuiBox
          display="flex"
          alignItems="center"
          justifyContent="center"
          width="100%"
          height="100%"
        >
          <SuiBox
            color="inherit"
            height="100%"
            display={{ xs: "none", sm: "none", md: "none", lg: "flex" }}
            pl={{ lg: 4 }}
          >
            {renderRoutes}
          </SuiBox>

          <SuiBox
            sx={{
              maxWidth: "400px",

              display: {
                xs: "none",
                sm: "block",
                md: "block",
                lg: "none",
                xl: "none",
                xxl: "block",
              },
              flexGrow: 1,
            }}
          >
            <Input
              ref={searchInputRef}
              addonBefore={
                account.account_type == "de" ? (
                  <div>
                    <SuiButton
                      variant="text"
                      color="secondary"
                      sx={{
                        "&:hover": { textDecoration: "none" },
                        padding: 0,
                        margin: 0,
                        fontSize: 10,
                        minWidth: 40,
                      }}
                      mx={0}
                      disableElevation
                      onClick={handleSearchMenuClick}
                    >
                      {searchType}{" "}
                      {openSearchMenu ? (
                        <KeyboardArrowUpIcon sx={{ width: 16, height: 16 }} />
                      ) : (
                        <KeyboardArrowDownIcon sx={{ width: 16, height: 16 }} />
                      )}
                    </SuiButton>
                    {renderSearchMenu()}
                  </div>
                ) : (
                  <SuiButton
                    variant="text"
                    color="secondary"
                    sx={{
                      "&:hover": { textDecoration: "none" },
                      padding: 0,
                      fontSize: 10,
                      minWidth: 40,
                    }}
                    mx={0}
                    disableElevation
                  >
                    {" "}
                    {searchType}
                  </SuiButton>
                )
              }
              suffix={
                <SearchIcon
                  color="secondary"
                  sx={{ cursor: "pointer", width: 16, height: 16 }}
                  onClick={onSearch}
                />
              }
              placeholder="Search"
              allowClear={false}
              size="large"
              onPressEnter={onSearch}
              style={{ lineHeight: 1.2, width: "100%" }}
            />
          </SuiBox>
        </SuiBox>

        <div className="flex items-center " style={{ minWidth: 160 }}>
          <Tooltip title={"Click to open search box"}>
            <IconButton
              size="medium"
              color="dark"
              sx={{
                ...navbarIconButton,
                display: {
                  xs: "inline-flex",
                  sm: "none",
                  md: "none",
                  lg: "inline-flex",
                  xl: "inline-flex",
                  xxl: "none",
                },
              }}
              onMouseDown={(e) => {
                setSearchModalVisible(true);
                e.stopPropagation();
                e.preventDefault();
              }}
            >
              <SearchIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title={store?.name}>
            <IconButton
              onClick={handleClick}
              size="small"
              aria-controls={open ? "account-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={open ? "true" : undefined}
              sx={navbarIconButton}
            >
              <Avatar
                src={store?.metadata?.avatar}
                variant="circle"
                sx={{ width: 30, height: 30, boxShadow: 2 }}
              />
              {/* <Avatar {...stringAvatar(account.first_name)} /> */}
            </IconButton>
          </Tooltip>
          {renderAccountMenu()}
          <IconButton
            size="medium"
            color="dark"
            sx={navbarIconButton}
            onMouseDown={(e) => {
              setRightDrawer(rightDrawer !== 2 ? 2 : 0);
            }}
          >
            <Badge color="error" badgeContent={ncount} size="small" invisible={ncount <= 0}>
              <NotificationsIcon />
            </Badge>
          </IconButton>
          <IconButton
            size="medium"
            color="dark"
            sx={navbarIconButton}
            onMouseDown={(e) => {
              setRightDrawer(rightDrawer !== 1 ? 1 : 0);
            }}
          >
            <Badge color="error" badgeContent={count} size="small" invisible={count <= 0}>
              <MailIcon />
            </Badge>
          </IconButton>
          <IconButton
            color="dark"
            sx={navbarIconButton}
            onClick={(e) => {
              navigate("/contactus");
            }}
          >
            <ContactSupportIcon />
          </IconButton>
        </div>
      </SuiBox>
      <SuiBox
        display={{ xs: "inline-block", sm: "inline-block", md: "inline-block", lg: "none" }}
        lineHeight={0}
        py={1.5}
        color="inherit"
        sx={{ cursor: "pointer", minWidth: 20 }}
        onClick={openMobileNavbar}
      >
        <Icon fontSize="default">{mobileNavbar ? "close" : "menu"}</Icon>
      </SuiBox>
      {mobileView && (
        <DefaultNavbarMobile routes={routes} open={mobileNavbar} close={closeMobileNavbar} />
      )}
      {searchModalVisible ? (
        <Prompt
          heading={`Search`}
          option={{
            fullWidth: true,
            size: "lg",
          }}
          text={
            <Input
              ref={searchInputRef}
              addonBefore={
                account.account_type == "de" ? (
                  <SuiBox>
                    <SuiButton
                      variant="text"
                      color="secondary"
                      sx={{
                        "&:hover": { textDecoration: "none" },
                        padding: 0,
                        margin: 0,

                        minWidth: 40,
                      }}
                      mx={0}
                      disableElevation
                      onClick={handleSearchMenuClick}
                    >
                      {searchType}{" "}
                      {openSearchMenu ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </SuiButton>
                    {renderSearchMenu()}
                  </SuiBox>
                ) : (
                  <SuiButton
                    variant="text"
                    color="secondary"
                    sx={{
                      "&:hover": { textDecoration: "none" },
                      padding: 0,

                      minWidth: 40,
                    }}
                    mx={0}
                    disableElevation
                  >
                    {" "}
                    {searchType}
                  </SuiButton>
                )
              }
              suffix={
                <SearchIcon color="secondary" sx={{ cursor: "pointer" }} onClick={onSearch} />
              }
              placeholder="Search"
              allowClear={false}
              size="large"
              onPressEnter={onSearch}
              style={{ lineHeight: 1.2, width: "100%" }}
            />
          }
          visible={searchModalVisible}
          okText={"Search"}
          okColor="dark"
          handleOk={onSearch}
          handleClose={() => setSearchModalVisible(false)}
        />
      ) : null}
      {tutorialVisible ? (
        <TutorialModal
          visible={tutorialVisible}
          handleClose={() => {
            setTutorialVisible(false);
          }}
        />
      ) : null}
    </SuiBox>
  );
}

// Setting default values for the props of DefaultNavbar
DefaultNavbar.defaultProps = {
  transparent: false,
  light: false,
  action: false,
};

// Typechecking props for the DefaultNavbar
DefaultNavbar.propTypes = {
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
  transparent: PropTypes.bool,
  light: PropTypes.bool,
  action: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.shape({
      type: PropTypes.oneOf(["external", "internal"]).isRequired,
      route: PropTypes.string.isRequired,
      color: PropTypes.oneOf([
        "primary",
        "secondary",
        "info",
        "success",
        "warning",
        "error",
        "dark",
        "light",
      ]),
      label: PropTypes.string.isRequired,
    }),
  ]),
};

export default DefaultNavbar;
